import styled from 'styled-components'
import { Box as MuiBox } from '@material-ui/core'
import { colors } from '@monorepo/theme'
import { StylesConfig } from 'react-select'

export const Box = MuiBox

export const MainBox = styled(Box)`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #ffffff;
  gap: 0.75rem;
`

/* Circular red delete button — sits at the far-left of every row */
export const DeleteButton = styled.button`
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid #e53e3e;
  background: transparent;
  color: #e53e3e;
  font-size: 1.2rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
  padding: 0;

  &:hover:not(:disabled) {
    background: #e53e3e;
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const ContentBox = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
`


export const SlideLabelBox = styled(Box)`
  min-width: 15rem;
  max-width: 15rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`

export const SlideTitleBox = styled(Box)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  max-width: 100%;
`

export const SlideTypeBadge = styled(Box)<{ itemType: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${({ itemType }) =>
        itemType === 'event' ? '#E8F4FD' : '#F0F7EC'};
  color: ${({ itemType }) =>
        itemType === 'event' ? '#1D78B8' : '#3A7D1A'};
  width: fit-content;
`

/* Thumbnail + its controls — stacked vertically, fixed width */
export const ThumbnailColumn = styled(Box)`
  width: 5rem;
  min-width: 5rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

/* Thumbnail image container */
export const ThumbnailBox = styled(Box)`
  width: 5rem;
  min-width: 5rem;
  height: 3.25rem;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ddd;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

/* Spinner overlay on the thumbnail while uploading */
export const ThumbnailUploadingOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: #333;
  font-weight: 500;
`

/* Small "Change" button styled like a text action — matches the row's compact feel */
export const ThumbnailChangeButton = styled.button`
  border: 1px solid #ccc;
  background: #fff;
  color: #333;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15rem 0.25rem;
  cursor: pointer;
  line-height: 1.1;
  transition: background 0.1s, border-color 0.1s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #999;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

/* Text link to reset custom thumbnail back to default */
export const ThumbnailResetLink = styled.button`
  border: none;
  background: transparent;
  color: ${colors.mediumGrey};
  font-size: 0.65rem;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  line-height: 1;

  &:hover:not(:disabled) {
    color: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const ThumbnailErrorText = styled(Box)`
  font-size: 0.65rem;
  color: #e53e3e;
  line-height: 1.1;
`

export const ItemSelectBox = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const SelectBox = styled(Box)`
  /* Prefer 28rem wide, can shrink but won't grow beyond that so the
     select doesn't expand to fill the full row when no LastModifiedBy */
  flex: 0 1 28rem;
  min-width: 14rem;
  display: flex;
  flex-direction: column;
`

export const LastModifiedByBox = styled(Box)`
  margin-left: auto;
  flex-shrink: 0;
  max-width: 18rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  font-size: 0.8rem;
  color: ${colors.mediumGrey};
`

export const ErrorBox = styled(Box)`
  display: flex;
  align-items: center;
  color: #ff0000;
  font-size: 0.85rem;
`

export const ButtonBox = styled(Box)`
  display: flex;
`

export const ButtonsBox = styled(Box)`
  display: flex;
  gap: 0.1rem;
`

interface AsyncSelectOption {
    label: string
    value: string
}

export const ItemSelectStyleConfig: StylesConfig<AsyncSelectOption, boolean> = {
    container: (base) => ({
        ...base,
        '&:not(:last-child)': {
            marginBottom: 16,
        },
    }),
    control: (base) => ({
        ...base,
        borderRadius: 6,
        minHeight: 47,
    }),
    placeholder: (base) => ({
        ...base,
        fontFamily: 'PFDINText',
        fontSize: 16,
        letterSpacing: -0.2,
        fontWeight: 'normal',
        color: colors.mediumGrey,
    }),
}

// ─── Confirm-delete modal ─────────────────────────────────────────────────────

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
`

export const ModalBox = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #111;
`

export const ModalBody = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.5;
`

export const ModalButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
`

export const ModalCancelButton = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  border: 1px solid #ccc;
  background: #fff;
  color: #333;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`

export const ModalDeleteButton = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  border: none;
  background: #e53e3e;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #c53030; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

